import { Link } from "react-router-dom";
import { aboutData, articles, categories } from "@/mocks/articles";
import ArticleCard from "@/components/base/ArticleCard";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb, { getAboutBreadcrumb } from "@/components/feature/Breadcrumb";

export default function About() {
  const latestArticles = [...articles]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: aboutData.brandName,
    url: "https://hzljbj.cn",
    description: aboutData.introduction,
    contactPoint: {
      "@type": "ContactPoint",
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      contactType: "customer service",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: aboutData.contact.address,
      addressLocality: "北京",
      addressRegion: "北京",
      addressCountry: "CN",
    },
    sameAs: [
      "https://weibo.com/51heiliao",
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "51黑料吃瓜网是什么？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "51黑料吃瓜网是国内领先的娱乐资讯聚合平台，致力于为网友提供最新、最全、最热的吃瓜爆料内容，覆盖娱乐圈、网红圈、社会热点等多个领域，每日实时更新。",
        },
      },
      {
        "@type": "Question",
        name: "51黑料的内容来源可靠吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "我们严格审核信息来源，确保每一条爆料的真实性。编辑团队会对每条内容进行多方核实，不做虚假新闻，不传播谣言。",
        },
      },
      {
        "@type": "Question",
        name: "如何联系51黑料团队？",
        acceptedAnswer: {
          "@type": "Answer",
          text: `您可以通过以下方式联系：邮箱 ${aboutData.contact.email}，微信 ${aboutData.contact.wechat}，微博 ${aboutData.contact.weibo}，或拨打客服电话 ${aboutData.contact.phone}。`,
        },
      },
      {
        "@type": "Question",
        name: "51黑料每日更新几次？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "我们的编辑团队7×24小时不间断追踪热点，确保用户能在第一时间获取最新资讯。热门事件发生后，通常在半小时内就会有相关报道。",
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-8">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={getAboutBreadcrumb()} />
          </div>

          {/* Hero */}
          <section className="relative mt-4">
            <div className="relative h-[300px] md:h-[400px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20elegant%20modern%20office%20interior%20with%20warm%20crimson%20and%20amber%20accent%20colors%2C%20minimalist%20design%2C%20soft%20natural%20light%2C%20creative%20workspace%20atmosphere%2C%20professional%20environment%2C%20clean%20composition&width=1400&height=400&seq=about-hero-01&orientation=landscape"
                alt="51黑料吃瓜网 - 关于我们"
                className="w-full h-full object-cover object-top"
                title="51黑料吃瓜网 - 关于我们"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h1 className="font-heading text-2xl md:text-4xl text-background-50">
                  {aboutData.brandName}
                </h1>
                <p className="mt-2 text-sm md:text-lg text-background-200 max-w-xl">
                  {aboutData.slogan}
                </p>
              </div>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-xl text-foreground-900 mb-4">
                  <a href="#brand-intro">品牌介绍</a>
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {aboutData.introduction}
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {aboutData.history}
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {aboutData.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="p-5 bg-background-100 rounded-lg text-center border border-background-200/70"
                  >
                    <div className="font-heading text-2xl md:text-3xl text-primary-500">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-xs text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-12">
            <h2 className="font-heading text-xl text-foreground-900 text-center mb-6">
              <a href="#core-values">核心价值观</a>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutData.values.map((value) => (
                <div
                  key={value.title}
                  className="p-5 bg-background-100 rounded-lg border border-background-200/70 text-center"
                >
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-check-double-line" />
                  </div>
                  <h3 className="mt-3 font-sans font-medium text-foreground-900 text-sm">
                    {value.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-12">
            <h2 className="font-heading text-xl text-foreground-900 mb-4">
              <a href="#reputation">用户口碑</a>
            </h2>
            <div className="space-y-4">
              {[
                { name: "吃瓜路人甲", rating: 5, text: "51黑料是我每天必刷的网站！更新速度快，内容质量高，吃瓜吃到停不下来。推荐给所有爱吃瓜的朋友！" },
                { name: "八卦小能手", rating: 5, text: "关注51黑料两年了，从最初的小站到现在的大平台，一直没让我失望。内容真实可靠，不会为了流量乱写。" },
                { name: "深夜吃瓜人", rating: 4, text: "界面简洁好看，文章写得也挺有意思的。希望以后能增加更多互动功能，比如评论区之类的。" },
                { name: "娱乐圈观察者", rating: 5, text: "相比于其他吃瓜平台，51黑料的内容更加专业，不会过度炒作，也不会传播未经证实的信息。值得信赖！" },
              ].map((review, idx) => (
                <div key={idx} className="p-5 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-foreground-900">{review.name}</span>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                      ))}
                      {Array.from({ length: 5 - review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-line text-foreground-300 text-sm" />
                      ))}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{review.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-12 pb-8">
            <h2 className="font-heading text-xl text-foreground-900 mb-4">
              <a href="#contact-info">联系我们</a>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: "ri-mail-line", label: "邮箱", value: aboutData.contact.email },
                { icon: "ri-wechat-line", label: "微信", value: aboutData.contact.wechat },
                { icon: "ri-weibo-line", label: "微博", value: aboutData.contact.weibo },
                { icon: "ri-phone-line", label: "电话", value: aboutData.contact.phone },
              ].map((item) => (
                <div key={item.label} className="p-4 bg-background-100 rounded-lg border border-background-200/70 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className={item.icon} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-foreground-500">{item.label}</div>
                    <div className="text-sm text-foreground-900 truncate">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}